import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface AddDeviceParams {
  deviceInput: string;
  setAsPrimary?: boolean;
}

export interface AddDeviceResult {
  success: boolean;
  deviceId: string;
  alreadyLinked: boolean;
  isPrimary?: boolean;
  enrollmentResult?: 'assigned' | 'pending_approval';
}

export function useAddDevice() {
  const { session, user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceInput, setAsPrimary }: AddDeviceParams): Promise<AddDeviceResult> => {
      if (!session?.access_token) {
        throw new Error('Ej autentiserad');
      }

      const { data, error } = await supabase.functions.invoke('add-device', {
        body: {
          deviceInput: deviceInput.trim().toUpperCase(),
          sessionToken: session.access_token,
          setAsPrimary: setAsPrimary ?? true,
        },
      });

      if (error) {
        throw new Error(error.message || 'Kunde inte lägga till enhet');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return data as AddDeviceResult;
    },
    onSuccess: () => {
      // Invalidate devices list — must match the full query key in useDevices
      queryClient.invalidateQueries({ queryKey: ['devices', user?.id] });
    },
  });
}
